import { useState, useMemo } from 'react';
import { AlertCircle, GitBranch, GitMerge, GitCommit, Trash2, CheckCircle, Clock, X, User, FolderKanban, ChevronDown, ChevronRight } from 'lucide-react';
import {
  useGetAbandonedWorkCandidates,
  usePatchAbandonedWorkCandidate,
  useGetProjects,
  type AbandonedWorkCandidate,
} from '@edison/api-client-react';
import { Link } from 'wouter';
import { Badge } from '@/components/ui/badge.js';
import { Card } from '@/components/ui/card.js';
import { TermTooltip } from '@/components/tour/index.js';

type BadgeVariant = 'warning' | 'destructive' | 'default';
const DETECTED_TYPE_META: Record<string, { label: string; icon: typeof GitBranch; badgeVariant: BadgeVariant }> = {
  unmerged_pr: { label: 'Unmerged PR', icon: GitMerge, badgeVariant: 'warning' },
  reverted_commit: { label: 'Reverted Commit', icon: GitCommit, badgeVariant: 'warning' },
  stale_branch: { label: 'Stale Branch', icon: GitBranch, badgeVariant: 'warning' },
  large_deletion: { label: 'Large Deletion', icon: Trash2, badgeVariant: 'destructive' },
};

function CandidateRow({
  candidate,
  onDismiss,
}: {
  candidate: AbandonedWorkCandidate;
  onDismiss: (id: string) => void;
}) {
  const meta = DETECTED_TYPE_META[candidate.detectedType];
  const developer = (candidate.metadata as { developer?: string | null } | null)?.developer;
  const daysOpen = (candidate.metadata as { daysOpen?: number } | null)?.daysOpen;
  const closedWithoutMerge = (candidate.metadata as { closedWithoutMerge?: boolean } | null)?.closedWithoutMerge;
  const artefactId = (candidate.metadata as { artefactId?: string } | null)?.artefactId;
  const dateStr = new Date(candidate.receivedAt).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div className="flex items-start justify-between gap-3 py-3 border-b last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          {meta && (
            <Badge variant={closedWithoutMerge ? 'destructive' : meta.badgeVariant} className="text-xs">
              <meta.icon className="h-3 w-3" aria-hidden="true" />
              {closedWithoutMerge ? 'Closed without merging' : meta.label}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground font-mono">{candidate.repoName}</span>
          <span className="text-xs text-muted-foreground">· {dateStr}</span>
          {daysOpen != null && (
            <span className="text-xs text-muted-foreground">· {daysOpen}d open</span>
          )}
        </div>
        {artefactId ? (
          <Link href={`/artefacts/${artefactId}`} className="text-sm text-foreground leading-snug hover:underline">
            {candidate.summary}
          </Link>
        ) : (
          <p className="text-sm text-foreground leading-snug">{candidate.summary}</p>
        )}
        {developer && (
          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <User className="h-3 w-3 shrink-0" aria-hidden="true" />
            {developer}
          </p>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-1.5">
        {candidate.status === 'confirmed' && (
          <Badge variant="positive" className="text-xs">
            <CheckCircle className="h-3 w-3" aria-hidden="true" />
            Captured as evidence
          </Badge>
        )}
        {candidate.status === 'pending' && (
          <Badge variant="default" className="text-xs">
            <Clock className="h-3 w-3" aria-hidden="true" />
            Processing
          </Badge>
        )}
        {candidate.status === 'dismissed' && (
          <Badge variant="default" className="text-xs">
            {candidate.status}
          </Badge>
        )}
        {(candidate.status === 'confirmed' || candidate.status === 'pending') && (
          <button
            onClick={() => onDismiss(candidate.id)}
            className="flex items-center justify-center h-7 w-7 rounded-md border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
            aria-label="Dismiss — remove from evidence"
            title="Dismiss — remove from evidence"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

function ProjectGroup({
  projectName,
  candidates,
  onDismiss,
}: {
  projectName: string;
  candidates: AbandonedWorkCandidate[];
  onDismiss: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const pending = candidates.filter((c) => c.status === 'pending').length;
  const confirmed = candidates.filter((c) => c.status === 'confirmed').length;
  const dismissed = candidates.filter((c) => c.status === 'dismissed').length;

  return (
    <Card className="overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        )}
        <FolderKanban className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="font-medium text-sm flex-1 truncate">{projectName}</span>
        <div className="flex items-center gap-2 shrink-0">
          {pending > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              {pending} awaiting capture
            </span>
          )}
          {confirmed > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              {confirmed} captured
            </span>
          )}
          {dismissed > 0 && (
            <span className="text-xs text-muted-foreground">{dismissed} dismissed</span>
          )}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-2 divide-y divide-border/50">
          {candidates.map((c) => (
            <CandidateRow key={c.id} candidate={c} onDismiss={onDismiss} />
          ))}
        </div>
      )}
    </Card>
  );
}

// Resolves a display name for a project group. Prefers the server-provided projectName
// (authoritative — covers projects of ANY status, not just the 'active' ones fetched for the
// confirm dialog below), then the locally-fetched active project's name, then repoOwner/repoName,
// and only as a last resort the raw id — this is what previously rendered as a bare UUID for
// e.g. completed/cancelled projects that fell outside the 'active'-only lookup.
function resolveGroupName(
  projectId: string,
  candidates: AbandonedWorkCandidate[],
  projectById: Map<string, { name: string }>,
): string {
  const first = candidates[0];
  return (
    first?.projectName ??
    projectById.get(projectId)?.name ??
    (first ? `${first.repoOwner}/${first.repoName}` : undefined) ??
    projectId
  );
}

export function AbandonedWorkQueuePage() {
  const { data, isLoading, error, refetch } = useGetAbandonedWorkCandidates();
  const { mutate: patchCandidate } = usePatchAbandonedWorkCandidate();
  const { data: projectsData } = useGetProjects({ status: 'active' });

  const [showDismissed, setShowDismissed] = useState(false);

  const allCandidates = data?.data?.items ?? [];
  const projects = projectsData?.data?.items ?? [];
  const projectById = new Map(projects.map((p) => [p.id, p]));

  const stats = useMemo(() => ({
    total: allCandidates.length,
    pending: allCandidates.filter((c) => c.status === 'pending').length,
    confirmed: allCandidates.filter((c) => c.status === 'confirmed').length,
    dismissed: allCandidates.filter((c) => c.status === 'dismissed').length,
  }), [allCandidates]);

  // Group by projectId — null goes into "Unassigned"
  const groups = useMemo(() => {
    const visible = showDismissed ? allCandidates : allCandidates.filter((c) => c.status !== 'dismissed');
    const map = new Map<string | null, AbandonedWorkCandidate[]>();
    for (const c of visible) {
      const key = c.projectId ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    // Sort: named projects first (alphabetically), unassigned last
    const entries = [...map.entries()].sort(([aKey, aCandidates], [bKey, bCandidates]) => {
      if (aKey === null) return 1;
      if (bKey === null) return -1;
      return resolveGroupName(aKey, aCandidates, projectById).localeCompare(resolveGroupName(bKey, bCandidates, projectById));
    });
    return entries;
  }, [allCandidates, showDismissed, projectById]);

  const handleDismiss = (id: string) => {
    patchCandidate({ id, action: 'dismiss' }, { onSuccess: () => void refetch() });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 rounded-lg skeleton" />
        <div className="grid grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 w-full rounded-lg skeleton" />)}
        </div>
        {[...Array(3)].map((_, i) => <div key={i} className="h-28 w-full rounded-lg skeleton" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Failed to load abandoned work.</span>
        <button onClick={() => refetch()} className="ml-auto underline text-xs">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          <TermTooltip term="Abandoned work">Abandoned work</TermTooltip>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Unmerged PRs, reverted commits, stale branches, and large deletions — approaches that were tried and
          didn't work — are captured automatically as evidence artefacts. Strong evidence of technical uncertainty
          for R&D. Dismiss removes noise from the evidence record; it doesn't touch the underlying commit or PR.
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border p-4">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Awaiting capture</p>
          <p className="text-2xl font-semibold tabular-nums">{stats.pending}</p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Captured evidence</p>
          <p className="text-2xl font-semibold tabular-nums text-green-700">{stats.confirmed}</p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">Dismissed</p>
          <p className="text-2xl font-semibold tabular-nums text-muted-foreground">{stats.dismissed}</p>
        </div>
      </div>

      {/* Project groups */}
      {groups.length === 0 ? (
        <div className="text-center py-12 rounded-lg border border-dashed">
          <GitBranch className="h-10 w-10 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
          <p className="font-medium">No abandoned work detected</p>
          <p className="text-sm text-muted-foreground mt-1">
            Edison checks GitHub events for closed-without-merge PRs, stale branches, large deletions, and revert
            commits — anything it finds is captured as evidence automatically.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(([projectId, candidates]) => {
            const name = projectId ? resolveGroupName(projectId, candidates, projectById) : 'Unassigned repo';
            return (
              <ProjectGroup
                key={projectId ?? '__unassigned__'}
                projectName={name}
                candidates={candidates}
                onDismiss={handleDismiss}
              />
            );
          })}
        </div>
      )}

      {/* Toggle dismissed */}
      {stats.dismissed > 0 && (
        <button
          onClick={() => setShowDismissed((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground underline"
        >
          {showDismissed ? 'Hide dismissed' : `Show ${stats.dismissed} dismissed`}
        </button>
      )}
    </div>
  );
}
